import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import classnames from "classnames";
import {
    UncontrolledAlert,
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Form,
    Input,
    Container,
    Row,
    Col,
    InputGroupAddon,
    InputGroupText,
    InputGroup,
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { createProducts } from 'api/productsApiCall';
import { getAllCategories } from 'api/categoryApiCall';
import { useTranslation } from '../../../i18n/context';

function AddProducts() {
    const [sidenavOpen, setSidenavOpen] = useState(true);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [allCategories, setAllCategories] = useState([]);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [products, setProducts] = useState([
        {
            product_name: '',
            description: '',
            price: '',
            category_name: '',
            category_id: '',
            currency: 'cedis', // Default currency
        }
    ]);

    const [error, setError] = useState(null);

    const addProductForm = () => {
        setProducts([...products, {
            product_name: '',
            description: '',
            price: '',
            category_name: '',
            category_id: '',
            currency: 'cedis', // Default currency for new product
        }]);
    };

    const removeProductForm = (index) => {
        const updatedProducts = products.filter((_, i) => i !== index);
        setProducts(updatedProducts);
    };

    const handleProductChange = (index, e) => {
        const { name, value } = e.target;
        const updatedProducts = [...products];
        updatedProducts[index][name] = value;

        if (name === 'price') {
            updatedProducts[index][name] = Number(value);
        }

        if (name === 'category_id') {
            const selectedCategory = allCategories.find(category => category._id === value);
            updatedProducts[index].category_name = selectedCategory ? selectedCategory.category_name : '';
        }

        setProducts(updatedProducts);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const invalidProduct = products.some(product => !product.category_id || product.price < 0);

        if (invalidProduct) {
            setError(t('product.seterror'));
            setShowErrorAlert(true);
            return;
        }

        const [errorProductsCall, successProductsCall] = await createProducts({ products });

        if (errorProductsCall || !successProductsCall) {
            setError(errorProductsCall);
            setShowErrorAlert(true);
        } else {
            setShowSuccessAlert(true);
            setTimeout(() => {
                navigate('/admin/product');
            }, 2000);
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            const [errorCategoriesCall, successCategoryCall] = await getAllCategories({});
            if (errorCategoriesCall || !successCategoryCall) {
                setAllCategories([]);
            } else {
                setAllCategories(successCategoryCall);
            }
        };

        fetchCategories();
    }, []);

    return (
        <>
            <SimpleHeader name={t('product.add_products')} />
            <Container className="mt--6" fluid>
                <Row>
                    <Col lg="12">
                        <div className="card-wrapper">
                            <Card>
                                <CardHeader>
                                    <h3 className="mb-0"> {t('product.add_products')}</h3>
                                    {showSuccessAlert && (
                                        <UncontrolledAlert color="success">
                                            <span className="alert-icon">
                                                <i className="ni ni-like-2" />
                                            </span>
                                            <span className="alert-text ml-1">
                                                <strong>{t('common.success')}</strong> {t('product.successful')}
                                            </span>
                                        </UncontrolledAlert>
                                    )}

                                    {showErrorAlert && (
                                        <UncontrolledAlert color="danger">
                                            <span className="alert-icon">
                                                <i className="ni ni-support-16" />
                                            </span>
                                            <span className="alert-text ml-1">
                                                <strong>{t('common.error')}</strong> {t('product.not_successfull')}
                                            </span>
                                        </UncontrolledAlert>
                                    )}
                                </CardHeader>
                                <CardBody>
                                    <Form onSubmit={handleSubmit}>
                                        {products.map((product, index) => (
                                            <div key={index}>
                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup className="row">
                                                            <Col md="11">
                                                                <Input
                                                                    type="select"
                                                                    onChange={(e) => handleProductChange(index, e)}
                                                                    value={product.category_id || ""}
                                                                    name="category_id"
                                                                >
                                                                    <option value="">{t('product.select')}</option>
                                                                    {allCategories.map((categoryData, categoryIndex) => (
                                                                        <option key={categoryIndex} value={categoryData._id}>
                                                                            {categoryData.category_name}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </Col>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <InputGroup className={classnames("input-group-merge")}>
                                                                <InputGroupAddon addonType="prepend">
                                                                    <InputGroupText>
                                                                        <i className="fa fa-address-book" />
                                                                    </InputGroupText>
                                                                </InputGroupAddon>
                                                                <Input
                                                                    placeholder={t('product.product_name')}
                                                                    type="text"
                                                                    name="product_name"
                                                                    value={product.product_name}
                                                                    onChange={(e) => handleProductChange(index, e)}
                                                                    required
                                                                />
                                                            </InputGroup>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup className="row">
                                                            <Col md="11">
                                                                <Input
                                                                    placeholder="Description"
                                                                    type="textarea"
                                                                    name="description"
                                                                    value={product.description}
                                                                    onChange={(e) => handleProductChange(index, e)}
                                                                    required
                                                                />
                                                            </Col>
                                                        </FormGroup>
                                                    </Col>

                                                    <Col md="3">
                                                        <FormGroup>
                                                            <InputGroup className={classnames("input-group-merge")}>
                                                                <InputGroupAddon addonType="prepend">
                                                                    <InputGroupText>
                                                                        <i className="fa fa-currency" />
                                                                    </InputGroupText>
                                                                </InputGroupAddon>
                                                                <Input
                                                                    type="select"
                                                                    name="currency"
                                                                    value={product.currency || ""}
                                                                    onChange={(e) => handleProductChange(index, e)}
                                                                    required
                                                                >
                                                                    <option value="">Select Currency</option>
                                                                    <option value="GHS">₵ - Ghanaian Cedi</option>
                                                                    <option value="NGN">₦ - Nigerian Naira</option>
                                                                    <option value="XOF">XOF - West African CFA Franc</option>
                                                                </Input>
                                                            </InputGroup>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="3">
                                                        <FormGroup>
                                                            <InputGroup className={classnames("input-group-merge")}>
                                                                <InputGroupAddon addonType="prepend">
                                                                    <InputGroupText>
                                                                        <i className="fa fa-money" />
                                                                    </InputGroupText>
                                                                </InputGroupAddon>
                                                                <Input
                                                                    placeholder={t('product.price')}
                                                                    type="number"
                                                                    name="price"
                                                                    value={product.price}
                                                                    onChange={(e) => handleProductChange(index, e)}
                                                                    required
                                                                />
                                                            </InputGroup>
                                                        </FormGroup>
                                                    </Col>


                                                </Row>

                                                {index > 0 && (
                                                    <Button color="danger" onClick={() => removeProductForm(index)}>{t('category.remove')}</Button>
                                                )}
                                                <hr />
                                            </div>
                                        ))}

                                        <Button onClick={addProductForm}>{t('product.add_another')}</Button>
                                        <Button className="btn-icon" color="primary" type="submit">
                                            <span className="btn-inner--text">{t('profile.submit')}</span>
                                        </Button>
                                    </Form>
                                </CardBody>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </Container>
        </>
    );
}


export default AddProducts;